import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../../contexts/AuthContext';
import { validatePasswordStrength, PASSWORD_REQUIREMENTS_MESSAGE } from '../../utils/password';

const RegisterPage = () => {
  const { register } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    if (!value) {
      setPasswordError('');
      return;
    }
    const validation = validatePasswordStrength(value);
    setPasswordError(validation.valid ? '' : validation.message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validatePasswordStrength(password);
    if (!validation.valid) {
      setPasswordError(validation.message);
      setError(validation.message);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        role: 'client',
        username,
        password,
        email,
        first_name: firstName,
        last_name: lastName,
        phone,
        address,
      };

      const result = await register(payload);
      if (result?.success) {
        setSuccess(result.message || 'Registration successful! Please login.');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(result?.message || 'Registration failed. Please check your details.');
      }
    } catch (err) {
      console.error('[RegisterPage ERROR]', err);
      setError('An unexpected error occurred while registering.');
    } finally {
      setLoading(false);
    }
  };

  // Nominatim lookup with debounce
  React.useEffect(() => {
    const controller = new AbortController();
    const q = (addressInput || '').trim();
    if (q.length < 3) {
      setAddressOptions([]);
      return () => controller.abort();
    }
    const t = setTimeout(async () => {
      try {
        setAddressLoading(true);
        const params = new URLSearchParams({
          q,
          format: 'jsonv2',
          addressdetails: '1',
          limit: '8',
        });
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        });
        if (!resp.ok) throw new Error('Address lookup failed');
        const data = await resp.json();
        const options = Array.isArray(data) ? data.map((r) => r.display_name).filter(Boolean) : [];
        setAddressOptions(options);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('[Nominatim] lookup error:', e);
        }
      } finally {
        setAddressLoading(false);
      }
    }, 400);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [addressInput]);

  return (
    <Container maxWidth="xs">
      <Paper sx={{ p: 3, mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          Register
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField label="Username" value={username} onChange={(event) => setUsername(event.target.value)} fullWidth margin="normal" required />
          <TextField label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} fullWidth margin="normal" required />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
            required
            error={Boolean(passwordError)}
            helperText={passwordError || PASSWORD_REQUIREMENTS_MESSAGE}
          />
          <TextField label="First Name" value={firstName} onChange={(event) => setFirstName(event.target.value)} fullWidth margin="normal" required />
          <TextField label="Last Name" value={lastName} onChange={(event) => setLastName(event.target.value)} fullWidth margin="normal" required />
          <TextField label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} fullWidth margin="normal" />
          <Autocomplete
            freeSolo
            options={addressOptions}
            value={address}
            onChange={(e, newValue) => setAddress(newValue || '')}
            inputValue={addressInput}
            onInputChange={(e, newInput) => {
              setAddressInput(newInput || '');
              setAddress(newInput || '');
            }}
            loading={addressLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Address"
                fullWidth
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {addressLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
            {loading ? 'Registering...' : 'Register'}
          </Button>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Button size="small" onClick={() => navigate('/')}>
              ← Back to home
            </Button>
            <Button size="small" onClick={() => navigate('/login')}>
              Already have an account? Login
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterPage;
